// =============================================================================
// EmojiPicker — popover used to choose a reaction emoji.
// =============================================================================
// Layout:
//   ┌───────────────────────────┐
//   │ [search box]              │
//   ├───────────────────────────┤
//   │ Quick: 👍 ❤️ 😂 🎉 😮 😢 │  ← always visible (skipped during search)
//   ├───────────────────────────┤
//   │ Smileys                   │
//   │ 😀 😁 😂 ...              │
//   │ Gestures                  │
//   │ 👍 👎 ...                 │
//   └───────────────────────────┘
//
// Auto-focuses search on open. Clicking any emoji or pressing Enter on the
// first search result picks that emoji and closes. Esc closes.
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { EMOJI_CATEGORIES, QUICK_REACTIONS, type EmojiEntry } from '../emoji';
import type { ServerEmoji } from '../types';

export function EmojiPicker({
  onPick,
  onClose,
  customEmojis,
}: {
  onPick: (emoji: string) => void;
  onClose: () => void;
  /** Optional per-server custom emoji. When provided, picking one calls
   *  onPick(":name:") so insertions go in as shortcodes. */
  customEmojis?: ServerEmoji[];
}) {
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus search field on open + Esc to close.
  useEffect(() => {
    inputRef.current?.focus();
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Flat search results when search box has content. Searches BOTH custom
  // emoji (matched by name substring) AND unicode (by keyword/glyph) so
  // typing "pep" surfaces a :pepe: custom emoji alongside any unicode hits.
  const searchResults = useMemo<EmojiEntry[] | null>(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    const results: EmojiEntry[] = [];
    // Custom emoji matches first — they're more relevant when present.
    if (customEmojis) {
      for (const ce of customEmojis) {
        if (ce.name.includes(q)) {
          results.push({ glyph: `:${ce.name}:`, keywords: ce.name });
          if (results.length >= 50) break;
        }
      }
    }
    if (results.length < 50) {
      for (const cat of EMOJI_CATEGORIES) {
        for (const e of cat.emoji) {
          if (e.keywords.includes(q) || e.glyph === q) {
            results.push(e);
            if (results.length >= 50) break;
          }
        }
        if (results.length >= 50) break;
      }
    }
    return results;
  }, [search, customEmojis]);

  function pick(emoji: string) {
    onPick(emoji);
    onClose();
  }

  return (
    <div
      className="hybrid-card w-72 max-h-[360px] flex flex-col"
      role="dialog"
      aria-label="Emoji picker"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="hybrid-card-header py-1.5!">
        <span className="text-[11px]">Pick a reaction</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="ml-auto text-[12px] leading-none w-5 h-5 hover:bg-white/10 rounded-sm flex items-center justify-center"
        >
          ×
        </button>
      </div>
      <div className="bg-surface p-2 border-b border-border-soft">
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && searchResults && searchResults.length > 0) {
              e.preventDefault();
              pick(searchResults[0]!.glyph);
            }
          }}
          placeholder="Search emoji…"
          className="w-full text-xs px-2 py-1 rounded-sm bg-elevated border border-border-soft outline-hidden focus:border-accent text-text"
        />
      </div>
      <div className="bg-surface flex-1 overflow-y-auto">
        {searchResults ? (
          <EmojiGrid emoji={searchResults} onPick={pick} customEmojis={customEmojis} />
        ) : (
          <>
            <div className="px-2 pt-2 pb-1 text-[9px] uppercase tracking-widest text-text-subtle font-bold">
              Quick
            </div>
            <EmojiGrid
              emoji={QUICK_REACTIONS.map((g) => ({ glyph: g, keywords: '' }))}
              onPick={pick}
              customEmojis={customEmojis}
            />
            {customEmojis && customEmojis.length > 0 && (
              <div>
                <div className="px-2 pt-2 pb-1 text-[9px] uppercase tracking-widest text-text-subtle font-bold">
                  This server
                </div>
                <CustomEmojiGrid customEmojis={customEmojis} onPick={pick} />
              </div>
            )}
            {EMOJI_CATEGORIES.map((cat) => (
              <div key={cat.name}>
                <div className="px-2 pt-2 pb-1 text-[9px] uppercase tracking-widest text-text-subtle font-bold">
                  {cat.name}
                </div>
                <EmojiGrid emoji={cat.emoji} onPick={pick} customEmojis={customEmojis} />
              </div>
            ))}
          </>
        )}
        {searchResults && searchResults.length === 0 && (
          <div className="text-xs text-text-muted text-center py-6">
            No matches.
          </div>
        )}
      </div>
    </div>
  );
}

function EmojiGrid({
  emoji,
  onPick,
  customEmojis,
}: {
  emoji: EmojiEntry[];
  onPick: (emoji: string) => void;
  customEmojis?: ServerEmoji[];
}) {
  // For shortcode entries (created by the search merger when matching
  // custom emoji), look up the URL in customEmojis to render the image.
  return (
    <div className="grid grid-cols-8 gap-px px-1 pb-1">
      {emoji.map((e, i) => {
        const m = /^:([a-z0-9_]{2,32}):$/.exec(e.glyph);
        const custom = m && customEmojis ? customEmojis.find((ce) => ce.name === m[1]) : null;
        return (
          <button
            key={`${e.glyph}-${i}`}
            type="button"
            onClick={() => onPick(e.glyph)}
            className="text-lg w-8 h-8 hover:bg-elevated rounded-sm flex items-center justify-center transition-colors"
            title={custom ? `:${custom.name}:` : e.keywords.split(' ')[0]}
          >
            {custom ? (
              <img
                src={custom.url}
                alt={`:${custom.name}:`}
                draggable={false}
                className="h-6 w-6 select-none"
                loading="lazy"
              />
            ) : (
              e.glyph
            )}
          </button>
        );
      })}
    </div>
  );
}

function CustomEmojiGrid({
  customEmojis,
  onPick,
}: {
  customEmojis: ServerEmoji[];
  onPick: (emoji: string) => void;
}) {
  return (
    <div className="grid grid-cols-8 gap-px px-1 pb-1">
      {customEmojis.map((ce) => (
        <button
          key={ce.id}
          type="button"
          onClick={() => onPick(`:${ce.name}:`)}
          className="w-8 h-8 hover:bg-elevated rounded-sm flex items-center justify-center transition-colors"
          title={`:${ce.name}:`}
        >
          <img
            src={ce.url}
            alt={`:${ce.name}:`}
            draggable={false}
            className="h-6 w-6 select-none"
            loading="lazy"
          />
        </button>
      ))}
    </div>
  );
}
